/**
 * SIM-1724 — a single collapsible skill row in the T2 atomic-save editor. Clicking the header
 * toggles the inline `SkillEditForm`. Deleted rows render greyed and struck-through with an Undo
 * affordance, since the delete is staged until Save; created and edited rows carry a status badge.
 */
import { Button, cn } from "@simplicity/ui";
import { SkillEditForm } from "./SkillEditForm";
import type { DraftRow, SkillFields } from "./skillsDraft";

interface SkillRowProps {
  row: DraftRow;
  expanded: boolean;
  onToggleExpand: () => void;
  onChange: (patch: Partial<SkillFields>) => void;
  onRequestDelete: () => void;
  onUndoDelete: () => void;
  onRemoveCreate: () => void;
}

function StatusBadge({ status }: { status: DraftRow["status"] }) {
  if (status === "created")
    return <span className="text-xs font-medium text-success">Added (unsaved)</span>;
  if (status === "edited")
    return <span className="text-xs font-medium text-primary">Edited (unsaved)</span>;
  return null;
}

function DeletedRow({ row, onUndoDelete }: { row: DraftRow; onUndoDelete: () => void }) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3 opacity-60">
      <div className="min-w-0">
        <div className="truncate font-medium text-foreground line-through">{row.name}</div>
        <div className="text-xs text-muted-foreground">Will be deleted on Save</div>
      </div>
      <Button variant="outline" size="sm" onClick={onUndoDelete}>
        Undo
      </Button>
    </li>
  );
}

export function SkillRow(props: SkillRowProps) {
  const { row, expanded, onToggleExpand, onChange, onRequestDelete, onUndoDelete, onRemoveCreate } =
    props;

  if (row.status === "deleted") {
    return <DeletedRow row={row} onUndoDelete={onUndoDelete} />;
  }

  const deleteHandler = row.id ? onRequestDelete : onRemoveCreate;
  const deleteLabel = row.id ? "Delete" : "Remove";

  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          className="min-w-0 flex-1 text-left"
        >
          <div className="flex items-center gap-2">
            <span aria-hidden className={cn("text-muted-foreground", expanded && "rotate-90")}>
              ▶
            </span>
            <span className="truncate font-medium text-foreground">
              {row.name || "(unnamed skill)"}
            </span>
            <StatusBadge status={row.status} />
          </div>
          {!expanded && row.description && (
            <div className="ml-6 truncate text-sm text-muted-foreground">{row.description}</div>
          )}
        </button>
        <Button variant="destructive" size="sm" onClick={deleteHandler}>
          {deleteLabel}
        </Button>
      </div>
      {expanded && (
        <div className="mt-3">
          <SkillEditForm
            idPrefix={`skill-${row.key}`}
            fields={{ name: row.name, description: row.description, body: row.body }}
            onChange={onChange}
          />
        </div>
      )}
    </li>
  );
}
